import { useCallback, useContext, useEffect, useRef, useState } from "react";
import styles from "../../SizeAndWeightStyles.module.css";
import { useStyles } from "../../../../../Styles/styles";
import { SetupContext } from "../../../../../Contexts/SetupContext";
import SingleDropDown from "../../../../SingleDropDown";
import { useMutation } from "@apollo/client";
import deleteButton from "../../../../../Assets/deleteButton.png";
import Required from "../../Required/Required";
import { handleUuid } from "../../../../../utils/utils";
import { axleIndexToName } from "../../../utils/utils";
import GroupSubTotals from "../GroupSubTotals/GroupSubTotals";
import { ADD_OR_UPDATE_AXLE_GROUP_IN_SIZE_AND_WEIGHT } from "../../../Mutations/AddOrUpdateAxleGroupInSizeAndWeight";
import { ADD_OR_UPDATE_AXLE_IN_AXLE_GROUP } from "../../../Mutations/AddOrUpdateAxleInAxleGroup";
import { REMOVE_AXLE_FROM_AXLE_GROUP } from "../../../Mutations/RemoveAxleFromAxleGroup";
import AxleGroupWeight from "../AxleGroupWeight/AxleGroupWeight";
import AxleWeightRow from "../AxleWeightRow/AxleWeightRow";
import NumberField from "../../NumberFields/NumberFields";

const AxleGroup = ({
  steerAxleLimit,
  mostOverWeightAxleGroup,
  removeAxleGroup,
  sizeAndWeightId,
  platformWeigh,
  group,
  canEdit,
  userId,
  dispatch,
  userRoles,
  missingFields,
  startingAxleIndex,
  disabled,
  updateGrossWeight,
  nightMode,
  locked,
}) => {
  const { setup } = useContext(SetupContext);
  const { setupLists } = setup || {};
  const { axleGroupTypes } = setupLists || [];
  const classes = useStyles();
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  let localLeftSubtotal = 0;
  let localRightSubtotal = 0;

  if (!platformWeigh) {
    try {
      const { left, right } = (group?.axles || []).reduce(
        (acc, axle) => {
          return {
            left: acc.left + (+axle.leftAxleWeight || 0),
            right: acc.right + (+axle.rightAxleWeight || 0),
          };
        },
        { left: 0, right: 0 },
      );
      localLeftSubtotal = left;
      localRightSubtotal = right;
    } catch (e) {
      console.error("Error calculating left and right subtotals", e);
    }
  }
  const {
    overLimit,
    leftSubtotal,
    rightSubtotal,
    subtotal,
    axleGroupLimit,
    weight,
  } = group || {};

  const [updateAxleGroup] = useMutation(
    ADD_OR_UPDATE_AXLE_GROUP_IN_SIZE_AND_WEIGHT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: () => {
        // dispatch({ type: "REPLACE_AXLE_GROUP", payload: { value: data.addOrUpdateAxleGroupInSizeAndWeight.axleGroup } })
        updateCalculatedWeights();
        updateGrossWeight();
      },
    },
  );

  const updateCalculatedWeights = useCallback(() => {
    let update = false;
    const input = {
      axleGroupId: group?.id,
      sizeAndWeightId,
      originatorId: userId,
    };

    if (!platformWeigh) {
      if (+localLeftSubtotal !== leftSubtotal) {
        update = true;
        input.leftSubtotal = localLeftSubtotal;
      }
      if (+localRightSubtotal !== rightSubtotal) {
        update = true;
        input.rightSubtotal = localRightSubtotal;
      }
      if (+subtotal !== localLeftSubtotal + localRightSubtotal) {
        update = true;
        input.subtotal = localLeftSubtotal + localRightSubtotal;
      }
      if (
        localLeftSubtotal + localRightSubtotal - axleGroupLimit !==
        overLimit
      ) {
        update = true;
        input.overLimit =
          localLeftSubtotal + localRightSubtotal - axleGroupLimit;
      }
    }

    if (platformWeigh) {
      if (weight - axleGroupLimit !== overLimit) {
        update = true;
        input.overLimit = weight - axleGroupLimit;
      }
    }
    if (update) {
      try {
        //TODO: Add a dispatch here
        //iterate over the input object, ignore the keys: axleGroupId, sizeAndWeightId, originatorId. All other keys should be converted to objects like {propertyName: Object key name, value: Object key value}
        const inputArray = Object.keys(input)
          .filter(
            key =>
              !["axleGroupId", "sizeAndWeightId", "originatorId"].includes(key),
          )
          .map(key => {
            return { propertyName: key, value: input[key] };
          });
        dispatch({
          type: "UPDATE_MULTIPLE_AXLE_GROUP_PROPERTIES",
          payload: { axleGroupId: group?.id, properties: inputArray },
        });
        updateAxleGroup({
          variables: {
            input: input,
          },
        });
      } catch (e) {
        console.error("Error updating axle group name", e);
      }
    }
  }, [
    axleGroupLimit,
    dispatch,
    group?.id,
    leftSubtotal,
    localLeftSubtotal,
    localRightSubtotal,
    overLimit,
    platformWeigh,
    rightSubtotal,
    sizeAndWeightId,
    subtotal,
    updateAxleGroup,
    userId,
    weight,
  ]);

  const handleAxleGroupChange = useCallback(
    (value, action, payload) => {
      const addOrUpdateAxleGroupInSizeAndWeightInputs = {
        name: {
          name: "name",
          accessId: false,
          hasMany: false,
        },
        axleGroupType: {
          name: "axleGroupTypeId",
          accessId: true,
          hasMany: false,
        },
        axleGroupLimit: {
          name: "axleGroupLimit",
          accessId: false,
          hasMany: false,
        },
      };

      if (payload === null || value === undefined) return null;
      if (!group?.id) {
        alert("No Axle Group id passed to handleAxleGroupChange");
        return null;
      }
      let stateName = payload?.property;
      let mutationValue = value;
      const { name, accessId, hasMany } =
        addOrUpdateAxleGroupInSizeAndWeightInputs[stateName];
      payload.value = value;
      payload.axleGroupId = group?.id;
      dispatch({ type: action, payload });

      if (accessId && !hasMany) {
        mutationValue = +value?.id;
      }
      const input = {
        axleGroupId: group?.id,
        [name]: mutationValue,
        sizeAndWeightId,
        originatorId: userId,
      };

      try {
        updateAxleGroup({
          variables: {
            input: input,
          },
        });
      } catch (e) {
        console.error("Error updating axle group name", e);
      }

      if (stateName === "axleGroupType") {
        let { weightLimit } = value;
        if (value?.name.includes("Steer")) {
          weightLimit = steerAxleLimit;
        }
        dispatch({
          type: "UPDATE_AXLE_GROUP",
          payload: {
            axleGroupId: group?.id,
            value: weightLimit,
            property: "axleGroupLimit",
          },
        });
        try {
          updateAxleGroup({
            variables: {
              input: {
                axleGroupId: group?.id,
                axleGroupLimit: weightLimit,
                sizeAndWeightId,
                originatorId: userId,
              },
            },
          });
        } catch (e) {
          console.error("Error updating axle group limit", e);
        }
      }
    },
    [
      group?.id,
      dispatch,
      sizeAndWeightId,
      userId,
      updateAxleGroup,
      steerAxleLimit,
    ],
  );

  const [updateAxleInAxleGroup, { data, loading }] = useMutation(
    ADD_OR_UPDATE_AXLE_IN_AXLE_GROUP,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: () => {
        // dispatch({ type: "REPLACE_AXLE_GROUP", payload: { value: data.addOrUpdateAxleInAxleGroup.axleGroup } })
        updateGrossWeight();
        updateCalculatedWeights();
      },
    },
  );

  const [removeAxleFromAxleGroup] = useMutation(REMOVE_AXLE_FROM_AXLE_GROUP, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: () => {
      // dispatch({ type: "REPLACE_AXLE_GROUP", payload: { value: data.addOrUpdateAxleInAxleGroup.axleGroup } })
      updateGrossWeight();
      updateCalculatedWeights();
    },
  });

  const addAxle = useCallback(async () => {
    const axleGroupId = group?.id;

    const emptyAxle = {
      id: handleUuid(),
      leftAxleWeight: null,
      rightAxleWeight: null,
      name: "",
    };
    const updatedAxles = [...(group?.axles ?? []), emptyAxle];
    dispatch({
      type: "UPDATE_AXLE_GROUP",
      payload: {
        axleGroupId: group?.id,
        property: "axles",
        value: updatedAxles,
      },
    });
    try {
      const response = await updateAxleInAxleGroup({
        variables: {
          input: {
            axleGroupId,
            originatorId: userId,
          },
        },
      });
      dispatch({
        type: "UPDATE_AXLE",
        payload: {
          axleGroupId: group?.id,
          axleId: emptyAxle?.id,
          property: "id",
          value: response?.data?.addOrUpdateAxleInAxleGroup?.axle?.id,
        },
      });
    } catch (e) {
      console.error("Error adding axle to axle group", e);
    }
  }, [group, dispatch, updateAxleInAxleGroup, userId]);

  const removeAxle = axleId => {
    if (window.confirm("Are you sure you want to remove this axle?")) {
      const axleGroupId = group?.id;
      const removedAxle = group?.axles?.find(axle => axle?.id === axleId);
      if (removedAxle) {
        const axleId = removedAxle.id;

        try {
          removeAxleFromAxleGroup({
            variables: {
              input: {
                axleGroupId,
                axleId,
                originatorId: userId,
              },
            },
          });

          const updatedAxles = group?.axles?.filter(
            axle => axle?.id !== axleId,
          );
          dispatch({
            type: "UPDATE_AXLE_GROUP",
            payload: {
              axleGroupId: group?.id,
              property: "axles",
              value: updatedAxles,
            },
          });
        } catch (e) {
          console.error("Error removing axle from axle group", e);
        }
      }
    }
  };

  const updateAxleWeight = useCallback(
    (axleId, weight, side, axleGroupId, totalAxleWeight) => {
      if (!axleId || !axleGroupId) return null;
      if (side !== "leftAxleWeight" && side !== "rightAxleWeight") return null;
      if (weight === null || weight === undefined) return null;

      try {
        updateAxleInAxleGroup({
          variables: {
            input: {
              axleGroupId,
              axleId,
              originatorId: userId,
              [side]: +weight,
              totalAxleWeight: +totalAxleWeight,
            },
          },
        });
        dispatch({
          type: "UPDATE_MULTIPLE_AXLE_PROPERTIES",
          payload: {
            axleGroupId,
            axleId,
            properties: [
              { propertyName: side, value: +weight },
              { propertyName: "totalAxleWeight", value: +totalAxleWeight },
            ],
          },
        });
      } catch (e) {
        console.error("Error adding weight to axle in axle group", e);
      }
    },
    [dispatch, updateAxleInAxleGroup, userId],
  );

  //This useEffect adds an axle to the axle group if the group is empty
  useEffect(() => {
    if (
      group?.axles?.length === 0 &&
      !loading &&
      (data?.addOrUpdateAxleInAxleGroup?.axleGroup?.axles?.length ?? 0) ===
        0 &&
      !locked
    ) {
      addAxle();
    }
  }, [addAxle, data, group?.axles?.length, loading, locked]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (group?.axleGroupType?.name.includes("Steer") && !locked) {
      const value = steerAxleLimit;
      const action = "UPDATE_AXLE_GROUP";
      const payload = { property: "axleGroupLimit" };
      handleAxleGroupChange(value, action, payload);
    }
  }, [
    dispatch,
    group?.axleGroupType?.name,
    group.id,
    handleAxleGroupChange,
    steerAxleLimit,
    locked,
  ]);

  return (
    <div
      style={
        mostOverWeightAxleGroup?.id === group?.id
          ? { backgroundColor: nightMode ? "#3e3e3e" : "#dddddd" }
          : {}
      }
      className={`${styles["flexRow"]} ${styles["noWrap"]} ${styles["bordered"]}`}
    >
      <div className={`${styles["flexRow"]} ${styles["noWrap"]}`}>
        <button
          type="button"
          onClick={() => !disabled && removeAxleGroup(group?.id)}
          disabled={disabled}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            data-cy={"delete-axle-group-button-" + group?.id}
            src={deleteButton}
            className={disabled ? classes.disabledPngButton : classes.pngButton}
            alt="Delete Axle Group"
          />
        </button>
        <Required
          required={
            missingFields.some(field => field?.name === "axleGroupType") &&
            !group?.axleGroupType
          }
          message={
            missingFields.find(field => field?.name === "axleGroupType")?.error
          }
        >
          <SingleDropDown
            title={"Group\xa0Type"}
            value={group?.axleGroupType}
            options={axleGroupTypes}
            handleChange={e =>
              handleAxleGroupChange(e.target.value, "UPDATE_AXLE_GROUP", {
                property: "axleGroupType",
              })
            }
            userRoles={userRoles}
            selectStyle={classes.singleDropDownPaddingAdjustment}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            canEdit={canEdit}
            disabled={disabled}
          />
        </Required>
        <Required
          required={
            missingFields.some(field => field?.name === "axleGroupLimit") &&
            (!Number.isInteger(group?.axleGroupLimit) ||
              group?.axleGroupLimit < 0)
          }
          message={
            missingFields.find(field => field?.name === "axleGroupLimit")?.error
          }
        >
          <NumberField
            variant={"outlined"}
            label={"Axle\xa0Group\xa0Limit"}
            value={group?.axleGroupLimit}
            dispatch={dispatch}
            action="UPDATE_AXLE_GROUP"
            property={"axleGroupLimit"}
            payload={{ axleGroupId: group?.id, property: "axleGroupLimit" }}
            handleBlur={handleAxleGroupChange}
            lbs={true}
            disabled={group?.axleGroupType?.name?.includes("Steer") || disabled}
          />
        </Required>
      </div>
      <div className={`${styles["flexColumn"]} ${styles["flexAlignStart"]}`}>
        {platformWeigh ? (
          <AxleGroupWeight
            axleGroup={group}
            dispatch={dispatch}
            sizeAndWeightId={sizeAndWeightId}
            userId={userId}
            missingFields={missingFields}
            disabled={disabled}
            updateAxleGroup={updateAxleGroup}
          />
        ) : (
          group?.axles?.map((axle, index) => {
            const axleName = axleIndexToName[startingAxleIndex + index];
            return (
              <AxleWeightRow
                key={axle?.id}
                axle={axle}
                axleName={axleName}
                updateAxleWeight={updateAxleWeight}
                axleGroupId={group?.id}
                dispatch={dispatch}
                missingFields={missingFields}
                removeAxle={removeAxle}
                addAxle={addAxle}
                isLast={index === group?.axles?.length - 1}
                disabled={disabled}
              />
            );
          })
        )}
        {!platformWeigh && (
          <GroupSubTotals
            overLimit={overLimit}
            leftSubtotal={leftSubtotal}
            rightSubtotal={rightSubtotal}
            subtotal={subtotal}
          />
        )}
      </div>
    </div>
  );
};

export default AxleGroup;
